/**
 * @copyright
 * Copyright (c) 2022 Accretive Technology Group, Inc. All rights
 * reserved. For use only by Accretive Technology, its employees
 * and contractors. DO NOT DISTRIBUTE.
 */

export const REGEXP_ALLOWED_CHARACTERS = /^[\x20-\x7E]*$/; // all printable ASCII characters
export const REGEXP_SCRIPT_TAG = /^(?!.*<script([^>]*)?>.*?<\/script>).*/i;

export enum RequestStatus {
  IDLE = "IDLE",
  PENDING = "PENDING",
  SUCCESS = "SUCCESS",
  ERROR = "ERROR",
}

export enum Device {
  MOBILE = "MOBILE",
  TABLET = "TABLET",
  DESKTOP = "DESKTOP",
}

export enum ScrollStatus {
  INITIAL = "INITIAL",
  PAGE_HEADER = "PAGE_HEADER",
  CONTENT = "CONTENT",
  CONTENT_DASHBOARD = "CONTENT_DASHBOARD",
}

export const PAGE_CONTENT_MAX_WIDTH = 1200;
export const PAGE_CONTENT_PADDING = 15;

export const DASHBOARD_GLANCE_HEIGHT_DESKTOP = 180;
export const DASHBOARD_GLANCE_HEIGHT_TABLET = 240;
export const DASHBOARD_GLANCE_HEIGHT_MOBILE = 640;

export const DEVICE_BP_TABLET_MIN = 768;
export const DEVICE_BP_DESKTOP_MIN = PAGE_CONTENT_MAX_WIDTH;

export enum AccountInfoStatus {
  MISSING = 0,
  PENDING = 1,
  REJECTED = 2,
  APPROVED = 3,
  DRAFT = 4,
}

// state: auth.user.paymentInfo.review_status
export enum UserPaymentStatus {
  NOT_SUBMITTED = 0,
  NOT_YET_REVIEWED = 1,
  IN_REVIEW = 2,
  APPROVED_NOT_APPLIED = 3,
  IN_USE = 5,
}

export const UserComplianceDocumentsStatus = {
  NOT_FOUND: null,
  PENDING: 0,
  REJECTED: 1,
  APPROVED: 2,
  DRAFT: 3,
};

export enum UserNotification {
  PAXUM = 9,
  FIRST_CHOICE = 8,
  WIRE = 6,
  INCOMPLETE_WIRE = 17,
}

export const StreamateSites = [
  { siteid: 1, domain: "streamate.com", name: "Streamate" },
  { siteid: 256, domain: "streamen.com", name: "Streamen" },
  { siteid: 3088, domain: "tsmate.com", name: "Tsmate" },
];

export const SitePromotionIds = [1, 256, 3088, 10336];

export const SitePromotionMap: { [key: number]: string } = {
  1: "streamate.com",
  256: "streamen.com",
  3088: "tsmate.com",
  10336: "livehdcams.com",
};

// Callout text must same as locale callout key
export const PayoutPrograms = [
  { programId: 1, shortName: "35lrs", longName: "Lifetime Revenue Share", callout: "BEST" },
  { programId: 26, shortName: "75ps30lrs", longName: "Per Spender + Lifetime Revenue Share", callout: "" },
  { programId: 21, shortName: "30pj30lrs", longName: "Per Join + Lifetime Revenue Share", callout: "" },
  { programId: 16, shortName: "100ps", longName: "Per Spender", callout: "" },
  { programId: 11, shortName: "40pj", longName: "Per Join", callout: "" },
];

export const PayoutProgramNameMap: { [key: string]: string } = {
  "Lifetime Revenue Share": "LIFETIME_REVENUE",
  "Per Join": "PER_JOIN",
  "Per Join + Lifetime Revenue Share": "PER_JOIN_AND_LIFETIME",
  "Per Spender": "PER_SPENDER",
  "Per Spender + Lifetime Revenue Share": "PER_SPENDER_AND_LIFETIME",
};

export enum PayProgramType {
  "35LRS" = "1",
  "75PS" = "26",
  "30PJ30LRS" = "21",
  "100PS" = "16",
  "40PJ" = "11",
}

export const PayProgramTypeMap: { [key: string]: PayProgramType } = {
  "1": PayProgramType["35LRS"],
  "26": PayProgramType["75PS"],
  "21": PayProgramType["30PJ30LRS"],
  "16": PayProgramType["100PS"],
  "11": PayProgramType["40PJ"],
  "Lifetime Revenue Share": PayProgramType["35LRS"],
  "Per Spender + Lifetime Revenue Share": PayProgramType["75PS"],
  "Per Join + Lifetime Revenue Share": PayProgramType["30PJ30LRS"],
  "Per Spender": PayProgramType["100PS"],
  "Per Join": PayProgramType["40PJ"],
};

// TODO loader with cdn config
export enum Asset {
  FILES_AVAILABLE_PERFORMERS = "/cdn/files/AvailablePerformers.pdf",
  FILES_EPOCH_INTEGRATION = "/cdn/files/epoch_integration.pdf",
  FILES_ROCKETGATE_INTEGRATION = "/cdn/files/rocketgate_integration.pdf",
  FILES_CCBILL_INTEGRATION = "/cdn/files/ccbill_simple_integration.pdf",
  FILES_NETBILLING_INTEGRATION = "/cdn/files/netbilling_integration.pdf",
  FILES_SEGPAY_INTEGRATION = "/cdn/files/segpay_integration.pdf",
  FILES_BILL1ST_INTEGRATION = "/cdn/files/bill1st_integration.pdf",
  FILES_PAYSAFE_INTEGRATION = "/cdn/files/paysafe_integration.pdf",
  FILES_VENDO_INTEGRATION = "/cdn/files/vendo_integration.pdf",
  FILES_ONECLICK_EXAMPLE_PHP = "/cdn/files/OneclickEncoding.zip",
  FILES_AGGREGATOR_TOOL = "/cdn/files/Model list & Live Player.pdf",
}

export const JSS_FONT_FAMILY_PRIMARY = "'Open Sans', sans-serif";
export const JSS_FONT_FAMILY_SECONDARY = "'Raleway', sans-serif;";

export const JSS_MEDIA_MOBILE_MAX = `@media (max-width: ${DEVICE_BP_TABLET_MIN - 1}px)`;
export const JSS_MEDIA_TABLET_MIN = `@media (min-width: ${DEVICE_BP_TABLET_MIN}px)`;
export const JSS_MEDIA_TABLET_MAX = `@media (max-width: ${DEVICE_BP_DESKTOP_MIN - 1}px)`;
export const JSS_MEDIA_DESKTOP_MIN = `@media (min-width: ${DEVICE_BP_DESKTOP_MIN}px)`;
export const JSS_MEDIA_MOBILE_MINI_MAX = "@media (max-width: 370px)"; // like Galaxy Fold

export const JSS_MEDIA_LANDING_TABLET_MAX = "@media (max-width: 1000px)";

export enum FontSize {
  "SMALL" = "12px",
  "ERROR" = "13px",
  "DEFAULT" = "14px",
  "MEDIUM" = "16px",
  "LARGE" = "18px",
  "HEADING_1" = "20px",
  "HEADING_2" = "22px",
  "HEADING_3" = "24px",
  "HEADING_4" = "26px",
  "HEADING_5" = "30px",
  "HEADING_6" = "32px",
  "HEADING_7" = "36px",
  "HEADING_8" = "40px",
  "HEADING_9" = "44px",
  "HEADING_10" = "48px",
  "HEADING_11" = "60px",
  "HEADING_12" = "70px",
}

export enum TranslationLang {
  DE = "de",
  EN = "en",
  ES = "es",
  FR = "fr",
  IT = "it",
  PT = "pt",
  RO = "ro",
  RU = "ru",
}

export enum Cookie {
  PHPSESSID = "PHPSESSID",
  USERID = "userid",
}

// From the old client, should be updated when a new code is added in the API
export enum ApiErrorCode {
  SUCCESS = 200,
  BAD_REQUEST = 400,
  FORBIDDEN = 403,
  METHOD_NOT_ALLOWED = 405,
  INTERNAL_SERVER_ERROR = 500,
  NOT_IMPLEMENTED = 501,
  BAD_GATEWAY = 502,
  SERVICE_UNAVAILABLE = 503,
  GATEWAY_TIMEOUT = 504,

  LOGIN_INVALID_LOGIN_INFO = -100,
  LOGIN_UNVERIFIED_EMAIL = -101,

  ACCOUNT_DISABLED = -102,
  ACCOUNT_PASSWORD_CHANGE_FAIL = -103,
  ACCOUNT_CHANGES_REQUIRE_VALID_PASSWORD = -104,
  ACCOUNT_NOT_FOUND = -105,

  SIGNUP_ACCOUNT_ALREADY_EXISTS = -110,
  SIGNUP_EMAILS_DO_NOT_MATCH = -111,
  SIGNUP_PASSWORDS_DO_NOT_MATCH = -112,
  SIGNUP_ACCOUNT_CREATE_ERROR = -113,
  SIGNUP_NEED_TERMS_AGREEMENT = -114,
  SIGNUP_INVALID_CAPTCHA = -115,

  VERIFYEMAIL_INVALID_TOKEN = -120,
  VERIFYEMAIL_ALREADY_VERIFIED = -121,
  VERIFYEMAIL_TOKEN_EXPIRED = -122,
  VERIFYEMAIL_FAILED_TO_SEND_EMAIL = -123,

  MONEYHOLE_SAVE_ERROR = -130,

  GENERAL_NOT_LOGGED_IN = -400,
  GENERAL_INVALID_ENDPOINT = -404,
  GENERAL_INVALID_PARAMS = -405,

  GENERAL_UNKNOWN_ERROR = -500,
}

export const StatesByCountry = {
  CA: ["NL", "PE", "NS", "NB", "QC", "ON", "MB", "SK", "AB", "BC", "YT", "NT", "NU"],
  US: [
    "AL",
    "AK",
    "AZ",
    "AR",
    "CA",
    "CO",
    "CT",
    "DE",
    "DC",
    "FL",
    "GA",
    "HI",
    "ID",
    "IL",
    "IN",
    "IA",
    "KS",
    "KY",
    "LA",
    "ME",
    "MD",
    "MA",
    "MI",
    "MN",
    "MS",
    "MO",
    "MT",
    "NE",
    "NV",
    "NH",
    "NJ",
    "NM",
    "NY",
    "NC",
    "ND",
    "OH",
    "OK",
    "OR",
    "PA",
    "RI",
    "SC",
    "SD",
    "TN",
    "TX",
    "UT",
    "VT",
    "VA",
    "WA",
    "WV",
    "WI",
    "WY",
  ],
};

export const SepaCountries = [
  "AT",
  "BE",
  "BG",
  "CY",
  "HR",
  "CZ",
  "DK",
  "EE",
  "FI",
  "FR",
  "DE",
  "GR",
  "HU",
  "IE",
  "IT",
  "LV",
  "LT",
  "LU",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SI",
  "SK",
  "ES",
  "SE",
  "NO",
  "LI",
  "IS",
  "CH",
  "MC",
  "GB",
];

export enum Interval {
  TODAY = "today",
  YESTERDAY = "yesterday",
  LAST_7_DAYS = "last_7_days",
  LAST_WEEK = "last_week",
  LAST_MONTH = "last_month",
  THIS_MONTH = "this_month",
  LAST_30_DAYS = "last_30_days",
  LAST_3_PAYOUTS = "last_3_payout",
  PREV_PERIOD = "prev_period",
  CURRENT_PERIOD = "current_period",
  CUSTOM_DATES = "custom_dates",
  MIN_AND_MAX_DATES = "min_and_max_dates",
}

export const IntervalQueryMap: { [key: string]: Interval } = {
  today: Interval.TODAY,
  yesterday: Interval.YESTERDAY,
  last_7_days: Interval.LAST_7_DAYS,
  last_week: Interval.LAST_WEEK,
  last_month: Interval.LAST_MONTH,
  this_month: Interval.THIS_MONTH,
  last_30_days: Interval.LAST_30_DAYS,
  prev_period: Interval.PREV_PERIOD,
  current_period: Interval.CURRENT_PERIOD,
  custom_dates: Interval.CUSTOM_DATES,
  min_and_max_dates: Interval.MIN_AND_MAX_DATES,
};

export const MAX_DATE_INTERVAL = 180;

export enum DayTime {
  START = "00:00:00",
  END = "23:59:59",
}

export enum DeviceType {
  UNKNOWN = 0,
  DESKTOP = 1,
  MOBILE = 2,
  TABLET = 3,
  CONSOLE = 4,
  SMART_TV = 5,
  OTHER = 6,
}

export const DeviceTypeMap: { [key: string]: DeviceType } = {
  Unknown: DeviceType.UNKNOWN,
  Desktop: DeviceType.DESKTOP,
  Mobile: DeviceType.MOBILE,
  Tablet: DeviceType.TABLET,
  Console: DeviceType.CONSOLE,
  "Smart TV": DeviceType.SMART_TV,
  Other: DeviceType.OTHER,
};

export const BrowserStorageKey = {
  DASHBOARD: "_cb_dashboard_browser_storage_data",
  STATISTICS: "_cb_statistics_browser_storage_data",
  USER_GUIDE: "_cb_user_guide_browser_storage_data",
  NEWS: "_cb_news_browser_storage_data",
};

// file header signatures to check the original mime type of the images/files
export const FileHeaderHex: { mime: string; pattern: string }[] = [
  {
    mime: "image/png",
    pattern: "89504e47",
  },
  {
    mime: "image/jpeg",
    pattern: "ffd8ffe0",
  },
  {
    mime: "image/gif",
    pattern: "47494638",
  },
];

export enum SkinReviewStatus {
  DRAFT,
  PENDING,
  APPROVED,
  REJECTED,
}

export const MAX_FILE_SIZE_BYTES = 7500000;
